import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Star, MessageCircle, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import ProductCard from "../components/ProductCard";
import { steps, heroVideo, heroImage, categorySections, faqItems, siteInfo, aboutImage, aboutStats } from "../mock";
import { getProducts, getReviews, getSettings } from "../api";
import { fileUrl } from "../api";
import { useSeo } from "../hooks/useSeo";

const CountUp = ({ value, className }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const m = String(value).match(/^([\d.,]+)(.*)$/);
    if (!m) { setDisplay(value); return; }
    const suffix = m[2] || "";
    const decimals = m[1].includes(",") ? (m[1].split(",")[1] || "").length : 0;
    const target = parseFloat(m[1].replace(",", "."));
    const fmt = (n) => (decimals > 0 ? n.toFixed(decimals).replace(".", ",") : String(Math.round(n))) + suffix;
    setDisplay(fmt(0));
    let started = false;
    const el = ref.current;
    const run = () => {
      const dur = 1600; const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(fmt(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting && !started) { started = true; run(); io.disconnect(); } });
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return <span ref={ref} className={className}>{display}</span>;
};

const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 34.6 26.9 35.7 24 35.7c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.6 5.6C41.9 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

const ReviewBadge = ({ href, rating = "5,0", count = "120" }) => {
  const inner = (
    <>
      <GoogleG className="w-6 h-6" />
      <div className="text-left">
        <div className="flex items-center gap-1.5">
          <span className="font-display text-brand-navy text-sm leading-none">{rating}</span>
          <div className="flex">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-brand-yellow text-brand-yellow" />)}
          </div>
        </div>
        <span className="text-[11px] text-slate-500 font-semibold">{count} Google reviews</span>
      </div>
    </>
  );
  const cls = "inline-flex items-center gap-3 bg-white/95 rounded-2xl px-4 py-2.5 shadow-lg mt-7 transition-transform hover:-translate-y-0.5";
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" data-testid="hero-google-reviews" className={cls}>{inner}</a>
    : <div className={cls}>{inner}</div>;
};

const Home = () => {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [googleUrl, setGoogleUrl] = useState("");
  const [reviewsRating, setReviewsRating] = useState("5,0");
  const [reviewsCount, setReviewsCount] = useState("120");
  const { hash } = useLocation();

  useSeo({
    title: "Springkussen verhuur Nederland | Jump en Joy",
    description: "Springkussen verhuur door heel Nederland: springkussens, glijbanen en stormbanen huren voor elk feest. Levering, opbouw en ophalen inbegrepen. Reserveer online bij Jump en Joy.",
    keywords: "springkussen verhuur, springkussen huren, springkasteel huren, glijbaan huren, stormbaan huren, attractie verhuur Nederland, kinderfeest",
  });

  // FAQ structured data (JSON-LD) — real, standards-based SEO (not a hack)
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", "faq");
    s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => { document.head.querySelector('script[data-seo="faq"]')?.remove(); };
  }, []);

  useEffect(() => { getProducts().then(setProducts).catch(() => {}); }, []);
  useEffect(() => {
    getReviews().then(setReviews).catch(() => {});
    getSettings().then((s) => {
      setGoogleUrl(s.google_reviews_url || "");
      if (s.reviews_rating) setReviewsRating(s.reviews_rating);
      if (s.reviews_count) setReviewsCount(s.reviews_count);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  const toppers = products.slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="wrap pt-8">
        <div className="relative rounded-[2rem] overflow-hidden min-h-[620px] lg:min-h-[680px] flex items-center">
          <video className="absolute inset-0 w-full h-full object-cover" src={heroVideo} poster="/hero-poster.jpg" autoPlay muted loop playsInline preload="auto" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/85 via-brand-navy/55 to-brand-navy/25" />
          <div className="relative px-6 sm:px-12 lg:px-16 py-12 max-w-3xl animate-fade-up">
            <span className="inline-block bg-brand-pink text-white text-sm font-bold rounded-full px-5 py-2 mb-6">
              Springkussen verhuur in heel Nederland
            </span>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02]">
              Springkussen verhuur voor een dag <span className="text-[#5CC0F5]">vol vreugde</span>
            </h1>
            <p className="text-slate-100 text-lg sm:text-xl mt-6 max-w-xl">
              Huur springkussens, glijbanen en stormbanen voor elk feest. Veilig opgebouwd, vlot geleverd door heel Nederland en weer opgehaald, jij geniet en wij regelen de rest.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/onze-luchtkastelen" className="rounded-full bg-brand-blue hover:bg-brand-pink text-white font-bold px-8 py-4 text-lg transition-all hover:-translate-y-0.5 shadow-lg">
                Bekijk onze springkussens
              </Link>
              <Link to="/contact" className="rounded-full bg-white hover:bg-slate-100 text-brand-navy font-bold px-8 py-4 text-lg transition-all hover:-translate-y-0.5">
                Reserveer nu
              </Link>
            </div>
            <ReviewBadge href={googleUrl} rating={reviewsRating} count={reviewsCount} />
          </div>
        </div>
      </section>

      {/* Aanbod CTA banner met verticale foto-marquees */}
      <section className="wrap mt-12">
        {(() => {
          const covers = products.map((p) => ({ src: (p.images && p.images.length ? p.images[0] : p.image), slug: p.slug })).filter((x) => x.src);
          const pics = covers.length ? covers : [{ src: (categorySections[0] && categorySections[0].img), slug: "" }];
          const colA = pics;
          const colB = [...pics].reverse();
          const Col = ({ imgs, cls }) => (
            <div className="overflow-hidden">
              <div className={`flex flex-col gap-3 ${cls}`}>
                {[...imgs, ...imgs].map((p, i) => (
                  <Link key={i} to={p.slug ? `/onze-luchtkastelen/${p.slug}` : "/onze-luchtkastelen"} className="block rounded-2xl overflow-hidden">
                    <img src={fileUrl(p.src)} alt="" loading="lazy" className="w-full h-36 sm:h-40 object-cover hover:opacity-90 transition-opacity" />
                  </Link>
                ))}
              </div>
            </div>
          );
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden shadow-md bg-brand-navy" data-testid="aanbod-banner">
              {/* Content */}
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <span className="inline-block bg-brand-yellow text-brand-navy text-xs font-bold rounded-full px-4 py-1.5 self-start">Springkussens, glijbanen & meer</span>
                <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">Ontdek al onze attracties</h2>
                <p className="text-blue-50/90 mt-3">Van kleurrijke springkussens tot spectaculaire glijbanen en stormbanen, voor elk feest de perfecte keuze.</p>
                <Link to="/onze-luchtkastelen" className="inline-flex items-center gap-2 self-start mt-6 rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold px-7 py-3.5 transition-all hover:gap-3">
                  Bekijk ons aanbod <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              {/* Verticale marquees */}
              <div className="relative h-[300px] lg:h-[460px] grid grid-cols-2 gap-3 p-3 overflow-hidden">
                <Col imgs={colA} cls="vmarquee-up" />
                <Col imgs={colB} cls="vmarquee-down" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-brand-navy to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-brand-navy to-transparent" />
              </div>
            </div>
          );
        })()}
      </section>

      {/* Toppers */}
      <section className="wrap mt-20">
        <span className="inline-block bg-brand-pinklight text-brand-pink text-xs font-bold rounded-full px-4 py-1.5">Populair</span>
        <div className="flex items-end justify-between mt-3">
          <h2 className="font-display text-brand-navy text-3xl sm:text-4xl">Onze toppers</h2>
          <Link to="/onze-luchtkastelen" className="inline-flex items-center gap-2 text-brand-blue font-bold hover:gap-3 transition-all">
            Bekijk alles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {toppers.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
        <p className="text-slate-500 mt-6 max-w-2xl">Kies uit springkussens, glijbanen en stormbanen voor verjaardagen, school- en bedrijfsfeesten. Elke attractie wordt gloednieuw en veilig verankerd geleverd.</p>
      </section>

      {/* Over ons */}
      <section id="over-ons" className="wrap mt-24 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Image collage with floating badges */}
          <div className="relative" data-testid="about-image-block">
            <div className="absolute -top-5 -left-5 w-28 h-28 rounded-3xl bg-brand-yellow/30 -z-10 hidden sm:block" />
            <div className="absolute -bottom-6 -right-6 w-36 h-36 rounded-full bg-brand-pinklight -z-10 hidden sm:block" />
            <div className="rounded-[2rem] overflow-hidden shadow-2xl ring-8 ring-white">
              <video src="/about.mp4" poster="/about-poster.jpg" autoPlay muted loop playsInline preload="auto" className="w-full h-[360px] sm:h-[440px] object-cover" data-testid="about-video" />
            </div>
            {/* Floating rating badge */}
            <a href={googleUrl || "#"} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!googleUrl) e.preventDefault(); }} className="absolute -bottom-6 left-6 sm:left-10 bg-white rounded-2xl shadow-xl px-5 py-3.5 flex items-center gap-3 transition-transform hover:-translate-y-0.5" data-testid="about-rating-badge">
              <div className="w-11 h-11 rounded-xl bg-brand-yellow/20 flex items-center justify-center">
                <Star className="w-6 h-6 fill-brand-yellow text-brand-yellow" />
              </div>
              <div>
                <div className="font-display text-brand-navy text-lg leading-none">{reviewsRating} / 5</div>
                <div className="text-[11px] text-slate-500 font-semibold mt-1">{reviewsCount} tevreden klanten</div>
              </div>
            </a>
          </div>

          {/* Content */}
          <div>
            <span className="inline-block bg-brand-bluelight text-brand-blue text-xs font-bold rounded-full px-4 py-1.5">Over ons</span>
            <h2 className="font-display text-brand-navy text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">Springplezier sinds dag één</h2>
            <p className="text-slate-500 mt-4 leading-relaxed">Wat begon als een pure passie voor het creëren van onvergetelijke feesten, is uitgegroeid tot hét vertrouwde verhuurbedrijf voor veilige en kwalitatieve springkussens. Al vanaf de allereerste dag brengen we met vol enthousiasme kleur en energie naar elk evenement.</p>
            <p className="text-slate-500 mt-4 leading-relaxed">Met diezelfde gedrevenheid nemen we je vandaag de dag nog steeds alle zorgen uit handen. Wij regelen alles van A tot Z, zodat jij alleen nog maar hoeft te genieten!</p>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-16" data-testid="about-stats">
          {aboutStats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-brand-blue text-white text-center py-7 px-4 hover:bg-brand-bluedark transition-colors">
              <CountUp value={s.value} className="font-display text-3xl sm:text-4xl text-white block" />
              <div className="text-blue-50/90 text-xs sm:text-sm font-semibold mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
        {/* Reviews */}
        {reviews.length > 0 && (
          <div className="mt-20" data-testid="reviews-section">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-2 bg-brand-bluelight text-brand-blue text-xs font-bold rounded-full px-4 py-1.5">
                  <GoogleG className="w-4 h-4" /> Wat klanten zeggen
                </span>
                <h3 className="font-display text-brand-navy text-2xl sm:text-3xl mt-3">Echte verhalen van blije feestjes</h3>
              </div>
              {googleUrl && (
                <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-button" className="inline-flex items-center gap-2.5 rounded-full bg-white border border-slate-200 hover:border-brand-blue hover:shadow-md text-brand-navy font-bold px-5 py-3 transition-all shrink-0">
                  <GoogleG className="w-5 h-5" /> Bekijk al onze reviews op Google
                </a>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {reviews.slice(0, 6).map((rv) => (
                <div key={rv.id} className="rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col hover:shadow-md transition-shadow" data-testid={`home-review-${rv.id}`}>
                  <div className="flex items-center gap-3">
                    {rv.has_photo && rv.photo ? (
                      <img src={fileUrl(rv.photo)} alt={rv.name} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-brand-pinklight text-brand-pink flex items-center justify-center font-display text-lg">{(rv.name || "?").charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <p className="font-display text-brand-navy leading-tight">{rv.name}</p>
                      <div className="flex mt-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= rv.rating ? "fill-brand-yellow text-brand-yellow" : "text-slate-200"}`} />)}</div>
                    </div>
                  </div>
                  <p className="text-slate-500 text-sm mt-4 leading-relaxed flex-1">"{rv.text}"</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA steps */}
      <section className="wrap mt-24">
        <div className="rounded-[2rem] bg-brand-blue text-white p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">Klaar voor een onvergetelijk feest?</h2>
            <p className="text-blue-50 mt-4 max-w-md leading-relaxed">Vraag je springkussen, glijbaan of stormbaan vandaag nog aan. Wij denken met je mee over de beste keuze voor jouw locatie.</p>
            <Link to="/contact" className="inline-flex mt-7 rounded-full bg-white text-brand-navy font-bold px-7 py-3.5 hover:bg-slate-100 transition-colors">Vraag aan</Link>
          </div>
          <div className="space-y-3">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-4 bg-white/10 rounded-2xl px-5 py-4">
                <span className="w-9 h-9 rounded-full bg-white text-brand-blue font-display flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="font-semibold">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="wrap mt-24 mb-24 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Blue accent contact panel */}
          <div className="lg:col-span-2 rounded-[2rem] bg-brand-blue text-white p-8 sm:p-10 lg:sticky lg:top-28" data-testid="faq-contact-panel">
            <span className="inline-block bg-white/15 text-white text-xs font-bold rounded-full px-4 py-1.5">Hulp nodig?</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-tight">Staat jouw vraag er niet bij?</h2>
            <p className="text-blue-50/90 mt-4 leading-relaxed">Wij denken graag met je mee. Stuur ons een appje of bel ons, we reageren snel en persoonlijk.</p>
            <div className="flex flex-col gap-3 mt-8">
              <a href={`https://wa.me/${siteInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold px-6 py-3.5 transition-all hover:-translate-y-0.5">
                <MessageCircle className="w-5 h-5" /> WhatsApp ons
              </a>
              <a href={`tel:${siteInfo.phoneRaw}`} data-testid="faq-call-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-navy font-bold px-6 py-3.5 transition-all hover:-translate-y-0.5">
                <Phone className="w-5 h-5" /> Bel {siteInfo.phone}
              </a>
              <Link to="/contact" data-testid="faq-contact-link" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 hover:bg-white/10 text-white font-bold px-6 py-3.5 transition-colors">
                <ArrowRight className="w-5 h-5" /> Naar contactpagina
              </Link>
            </div>
          </div>

          {/* FAQ accordion */}
          <div className="lg:col-span-3">
            <span className="inline-block bg-brand-bluelight text-brand-blue text-xs font-bold rounded-full px-4 py-1.5">FAQ</span>
            <h2 className="font-display text-brand-navy text-3xl sm:text-4xl mt-4">Veelgestelde vragen</h2>
            <p className="text-slate-500 mt-4">Alles wat je moet weten voordat je een springkussen reserveert.</p>
            <Accordion type="single" collapsible className="mt-8">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-100">
                  <AccordionTrigger className="font-display text-brand-navy text-left hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-500 leading-relaxed whitespace-pre-line">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
